import React, { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  EditLocation,
  Image,
  Layers,
  Lock,
  Map,
  MyLocation,
  Place,
  RestartAlt,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material'
import API from '../../../../services/API'
import {
  eventMapCorners,
  eventMapLngLatBounds,
  eventMapToLngLat,
  formatLngLat,
  hasScaledEventMap,
  lngLatToEventMap,
  resolveMapView,
} from './eventMapGeo'

const defaultCenter = [-1.5, 52.5]
const OVERLAY_SOURCE = 'event-map-overlay'

// Returns [lng, lat] (map/GeoJSON order) or null.
export const parseCoordinatePair = (value) => {
  if (!value) return null
  if (Array.isArray(value) && value.length >= 2) {
    const lng = Number(value[0])
    const lat = Number(value[1])
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null
  }

  const parts = String(value)
    .trim()
    .split(/[,\s]+/)
    .map(Number)
    .filter(Number.isFinite)

  if (parts.length < 2) return null
  const [first, second] = parts
  if (Math.abs(first) <= 180 && Math.abs(second) <= 90) return [first, second]
  if (Math.abs(first) <= 90 && Math.abs(second) <= 180) return [second, first]
  return null
}

const markerElement = () => {
  const el = document.createElement('div')
  el.style.width = '28px'
  el.style.height = '28px'
  el.style.borderRadius = '999px'
  el.style.background = '#E32525'
  el.style.border = '4px solid white'
  el.style.boxShadow = '0 10px 24px rgba(0,0,0,0.35)'
  return el
}

const IncidentLocationPicker = ({
  coordinates,
  setCoordinates,
  eventMapCoordinates,
  setEventMapCoordinates,
  eventMap,
  initialMapView = 'map',
  compact = false,
  height,
  locked = false,
  onUnlock,
}) => {
  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const eventMapViewportRef = useRef(null)
  const eventMapImageRef = useRef(null)
  const eventMapDragRef = useRef(null)
  const lockedRef = useRef(locked)
  const eventMapRef = useRef(eventMap)
  const overlayUrlRef = useRef(null)
  const [mapView, setMapView] = useState(() =>
    resolveMapView(initialMapView, eventMap),
  )
  const [eventMapUrl, setEventMapUrl] = useState(null)
  const [eventMapZoom, setEventMapZoom] = useState(1)
  const [eventMapPan, setEventMapPan] = useState({ x: 0, y: 0 })
  const [eventMapImageMetrics, setEventMapImageMetrics] = useState(null)
  const [loadingCurrentLocation, setLoadingCurrentLocation] = useState(false)

  const scaled = hasScaledEventMap(eventMap)
  const isOverlay = mapView === 'overlay' && scaled
  const isGeographic = mapView === 'map' || isOverlay
  // A pin dropped on a to-scale plan is a real position too, so show it on
  // the geographic views even when no explicit GPS coordinates were stored.
  const selectedCoordinates =
    parseCoordinatePair(coordinates) ||
    eventMapToLngLat(eventMap, eventMapCoordinates)

  useEffect(() => {
    lockedRef.current = locked
  }, [locked])

  useEffect(() => {
    eventMapRef.current = eventMap
  }, [eventMap])

  useEffect(() => {
    setMapView(resolveMapView(initialMapView, eventMap))
  }, [initialMapView, eventMap?.s3key, eventMap?.drawnToScale])

  useEffect(() => {
    setEventMapZoom(1)
    setEventMapPan({ x: 0, y: 0 })
  }, [eventMapUrl])

  const getEventMapImageMetrics = () => {
    const viewport = eventMapViewportRef.current
    const image = eventMapImageRef.current
    if (!viewport || !image) return null

    const rect = viewport.getBoundingClientRect()
    const imageRatio =
      image.naturalWidth && image.naturalHeight
        ? image.naturalWidth / image.naturalHeight
        : rect.width / rect.height
    const viewportRatio = rect.width / rect.height
    const width =
      imageRatio > viewportRatio ? rect.width : rect.height * imageRatio
    const height =
      imageRatio > viewportRatio ? rect.width / imageRatio : rect.height

    return {
      width,
      height,
      left: (rect.width - width) / 2,
      top: (rect.height - height) / 2,
      viewportWidth: rect.width,
      viewportHeight: rect.height,
    }
  }

  const refreshEventMapImageMetrics = () => {
    const metrics = getEventMapImageMetrics()
    if (metrics) setEventMapImageMetrics(metrics)
  }

  useEffect(() => {
    if (mapView !== 'event' || !eventMapUrl) return undefined

    refreshEventMapImageMetrics()
    const viewport = eventMapViewportRef.current
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(refreshEventMapImageMetrics)
    observer.observe(viewport)

    return () => observer.disconnect()
  }, [mapView, eventMapUrl])

  useEffect(() => {
    if (mapRef.current) return

    // Open on the incident if it has a position, otherwise on the event site
    // (never on the call handler's own location — that used to be written
    // into the incident as if it were the patient's position).
    const startScaled = hasScaledEventMap(eventMapRef.current)
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      center: selectedCoordinates || defaultCenter,
      zoom: selectedCoordinates ? 15 : 6,
      attributionControl: true,
    })
    if (!selectedCoordinates && startScaled) {
      map.once('load', () =>
        map.fitBounds(eventMapLngLatBounds(eventMapRef.current), {
          padding: 24,
          duration: 0,
          maxZoom: 17,
        }),
      )
    }

    map.addControl(new maplibregl.NavigationControl(), 'bottom-left')
    map.on('click', (event) => {
      if (lockedRef.current) return
      const lngLat = [event.lngLat.lng, event.lngLat.lat]
      setCoordinates(formatLngLat(lngLat))
      // Keep the site-plan pin in step on a to-scale map (cleared when the
      // click lands outside the plan).
      if (hasScaledEventMap(eventMapRef.current)) {
        setEventMapCoordinates(lngLatToEventMap(eventMapRef.current, lngLat))
      }
    })

    mapRef.current = map

    return () => {
      markerRef.current?.remove()
      markerRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    map.resize()
    if (!isGeographic) return
    // Returning to a geographic view: frame the incident, or the site.
    if (selectedCoordinates) {
      map.jumpTo({ center: selectedCoordinates, zoom: Math.max(map.getZoom(), 15) })
    } else if (scaled) {
      map.fitBounds(eventMapLngLatBounds(eventMap), {
        padding: 24,
        duration: 0,
        maxZoom: 17,
      })
    }
  }, [mapView])

  useEffect(() => {
    if (!mapRef.current) return

    if (!selectedCoordinates) {
      markerRef.current?.remove()
      markerRef.current = null
      return
    }

    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ element: markerElement() })
        .setLngLat(selectedCoordinates)
        .addTo(mapRef.current)
      return
    }

    markerRef.current.setLngLat(selectedCoordinates)
  }, [coordinates, eventMapCoordinates?.[0], eventMapCoordinates?.[1]])

  // Lay the site plan over the live map in overlay view.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return undefined

    const removeOverlay = () => {
      if (map.getLayer(OVERLAY_SOURCE)) map.removeLayer(OVERLAY_SOURCE)
      if (map.getSource(OVERLAY_SOURCE)) map.removeSource(OVERLAY_SOURCE)
      overlayUrlRef.current = null
    }

    if (!isOverlay || !eventMapUrl) {
      if (map.loaded()) removeOverlay()
      return undefined
    }

    const addOverlay = () => {
      if (overlayUrlRef.current !== eventMapUrl) removeOverlay()
      if (!map.getSource(OVERLAY_SOURCE)) {
        map.addSource(OVERLAY_SOURCE, {
          type: 'image',
          url: eventMapUrl,
          coordinates: eventMapCorners(eventMap),
        })
        map.addLayer({
          id: OVERLAY_SOURCE,
          type: 'raster',
          source: OVERLAY_SOURCE,
          paint: { 'raster-opacity': eventMap.opacity ?? 0.7 },
        })
        overlayUrlRef.current = eventMapUrl
      }
    }

    if (map.loaded()) addOverlay()
    else map.once('load', addOverlay)

    return () => {
      map.off('load', addOverlay)
    }
  }, [isOverlay, eventMapUrl, eventMap?.bounds, eventMap?.opacity])

  useEffect(() => {
    if (!eventMap?.s3key || !(mapView === 'event' || isOverlay)) {
      setEventMapUrl(null)
      return
    }

    API.post('/gets3downloadurl', {
      key: eventMap.s3key,
      inlineView: true,
      expires: 300,
    })
      .then((res) => setEventMapUrl(res.data))
      .catch(() => setEventMapUrl(null))
  }, [eventMap?.s3key, mapView === 'event' || isOverlay])

  const useCurrentLocation = () => {
    if (locked) return
    if (!navigator.geolocation) return
    setLoadingCurrentLocation(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = [
          position.coords.longitude,
          position.coords.latitude,
        ]
        setCoordinates(formatLngLat(next))
        if (hasScaledEventMap(eventMapRef.current)) {
          setEventMapCoordinates(lngLatToEventMap(eventMapRef.current, next))
        }
        mapRef.current?.flyTo({ center: next, zoom: 16 })
        setLoadingCurrentLocation(false)
      },
      () => setLoadingCurrentLocation(false),
      { enableHighAccuracy: true, timeout: 8000 },
    )
  }

  const handleEventMapClick = (event) => {
    if (locked) return
    if (eventMapDragRef.current?.dragged) return

    const rect = eventMapViewportRef.current?.getBoundingClientRect()
    const metrics = getEventMapImageMetrics()
    if (!rect || !metrics) return

    const centerX = metrics.viewportWidth / 2
    const centerY = metrics.viewportHeight / 2
    const localX =
      centerX +
      (event.clientX - rect.left - centerX - eventMapPan.x) / eventMapZoom
    const localY =
      centerY +
      (event.clientY - rect.top - centerY - eventMapPan.y) / eventMapZoom
    const x = Number(
      Math.min(1, Math.max(0, (localX - metrics.left) / metrics.width)).toFixed(4),
    )
    const y = Number(
      Math.min(1, Math.max(0, (localY - metrics.top) / metrics.height)).toFixed(4),
    )

    setEventMapCoordinates([x, y])
    // On a to-scale plan the pin is a real position — store it so crews can
    // navigate to it.
    const lngLat = eventMapToLngLat(eventMap, [x, y])
    if (lngLat) setCoordinates(formatLngLat(lngLat))
  }

  const handleEventMapPointerDown = (event) => {
    if (!eventMapUrl) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    eventMapDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      pan: eventMapPan,
      dragged: false,
    }
  }

  const handleEventMapPointerMove = (event) => {
    const drag = eventMapDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (Math.abs(dx) + Math.abs(dy) > 4) {
      drag.dragged = true
    }

    setEventMapPan({
      x: drag.pan.x + dx,
      y: drag.pan.y + dy,
    })
  }

  const handleEventMapPointerUp = (event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId)

    window.setTimeout(() => {
      eventMapDragRef.current = null
    }, 0)
  }

  const handleEventMapWheel = (event) => {
    if (!eventMapUrl) return
    event.preventDefault()
    updateEventMapZoom(eventMapZoom + (event.deltaY < 0 ? 0.2 : -0.2))
  }

  const updateEventMapZoom = (nextZoom) => {
    setEventMapZoom(Math.min(4, Math.max(1, nextZoom)))
  }

  const resetEventMapView = () => {
    setEventMapZoom(1)
    setEventMapPan({ x: 0, y: 0 })
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mapView}
          onChange={(_, next) => next && setMapView(next)}
          sx={{
            '& .MuiToggleButton-root': {
              gap: 0.5,
              textTransform: 'none',
              fontWeight: 900,
            },
          }}
        >
          <ToggleButton value="map">
            <Map fontSize="small" />
            Map
          </ToggleButton>
          <ToggleButton value="event">
            <Image fontSize="small" />
            Event
          </ToggleButton>
          <ToggleButton
            value="overlay"
            disabled={!scaled}
            title={scaled ? undefined : 'Needs an event map drawn to scale'}
          >
            <Layers fontSize="small" />
            Overlay
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          size="small"
          startIcon={
            loadingCurrentLocation ? (
              <CircularProgress size={14} />
            ) : (
              <MyLocation />
            )
          }
          onClick={useCurrentLocation}
          disabled={locked || loadingCurrentLocation}
          sx={{ textTransform: 'none', fontWeight: 900 }}
        >
          Current location
        </Button>

        {locked && (
          <Button
            size="small"
            variant="contained"
            startIcon={<EditLocation />}
            onClick={onUnlock}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#239AA4',
            }}
          >
            Edit location
          </Button>
        )}

        {selectedCoordinates && (
          <Chip
            icon={<Place />}
            label={`${selectedCoordinates[1].toFixed(5)}, ${selectedCoordinates[0].toFixed(5)}`}
            size="small"
          />
        )}
      </Stack>

      <Box
        sx={{
          height: height ?? (compact ? 280 : 360),
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          position: 'relative',
          backgroundColor: '#F8FAFC',
        }}
      >
        <Box
          ref={mapContainer}
          sx={{
            width: '100%',
            height: '100%',
            display: isGeographic ? 'block' : 'none',
          }}
        />

        {isOverlay && !locked && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              pointerEvents: 'none',
            }}
          >
            <Layers sx={{ fontSize: 16, color: 'white' }} />
            <Typography variant="caption" fontWeight={900} color="white">
              Site plan overlaid on the live map — click to set the incident
              position
            </Typography>
          </Stack>
        )}

        {mapView === 'event' && (
          <Box
            ref={eventMapViewportRef}
            onClick={handleEventMapClick}
            onPointerDown={handleEventMapPointerDown}
            onPointerMove={handleEventMapPointerMove}
            onPointerUp={handleEventMapPointerUp}
            onPointerCancel={handleEventMapPointerUp}
            onWheel={handleEventMapWheel}
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              cursor: locked
                ? 'default'
                : eventMapUrl
                  ? 'grab'
                  : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#E2E8F0',
              touchAction: 'none',
            }}
          >
            {eventMapUrl ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  transform: `translate(${eventMapPan.x}px, ${eventMapPan.y}px) scale(${eventMapZoom})`,
                  transformOrigin: 'center center',
                }}
              >
                <Box
                  ref={eventMapImageRef}
                  component="img"
                  src={eventMapUrl}
                  alt={eventMap?.fileName || 'Event map'}
                  draggable={false}
                  onLoad={refreshEventMapImageMetrics}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />

                {eventMapCoordinates?.length === 2 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: eventMapImageMetrics
                        ? eventMapImageMetrics.left +
                          eventMapCoordinates[0] * eventMapImageMetrics.width
                        : `${eventMapCoordinates[0] * 100}%`,
                      top: eventMapImageMetrics
                        ? eventMapImageMetrics.top +
                          eventMapCoordinates[1] * eventMapImageMetrics.height
                        : `${eventMapCoordinates[1] * 100}%`,
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      backgroundColor: '#E32525',
                      border: '4px solid white',
                      boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            ) : (
              <Alert severity="info">No event map has been uploaded.</Alert>
            )}

            {eventMapUrl && (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: 'rgba(15,23,42,0.88)',
                    color: 'white',
                    pointerEvents: 'none',
                  }}
                >
                  <Place sx={{ fontSize: 16, color: 'white' }} />
                  <Typography variant="caption" fontWeight={900} color="white">
                    {locked
                      ? 'Unlock editing first'
                      : 'Click once to reposition the incident location'}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    backgroundColor: 'rgba(15,23,42,0.88)',
                    borderRadius: 2,
                    p: 0.5,
                  }}
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <Button
                    size="small"
                    onClick={() => updateEventMapZoom(eventMapZoom - 0.25)}
                    sx={{ minWidth: 34, color: 'white' }}
                  >
                    <ZoomOut fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    onClick={() => updateEventMapZoom(eventMapZoom + 0.25)}
                    sx={{ minWidth: 34, color: 'white' }}
                  >
                    <ZoomIn fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    onClick={resetEventMapView}
                    sx={{ minWidth: 34, color: 'white' }}
                  >
                    <RestartAlt fontSize="small" />
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        )}

        {mapView === 'event' && !locked && eventMapUrl && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              pointerEvents: 'none',
            }}
          >
            <Typography variant="caption" fontWeight={800} color="white">
              Drag to move around the map. Use +/- to zoom.
            </Typography>
          </Stack>
        )}

        {mapView === 'event' && locked && eventMapUrl && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              pointerEvents: 'none',
            }}
          >
            <Typography variant="caption" fontWeight={800} color="white">
              Drag and zoom to inspect. Press Edit location before moving the
              incident.
            </Typography>
          </Stack>
        )}

        {locked && isGeographic && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              pointerEvents: 'none',
            }}
          >
            <Lock sx={{ fontSize: 16, color: 'white' }} />
            <Typography variant="caption" fontWeight={900} color="white">
              Location locked
            </Typography>
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

export default IncidentLocationPicker
